/**
 * Shared metric formatter for dashboard widgets — ported from flank-20.
 * Keeps KPI / table / chart cells rendering identical values across widgets.
 * Defers label + format to the canonical metric catalog so adding a metric is
 * a one-file change.
 */
import { CTR_CLICKABLE_NOTE } from '../../ReportingV2/format';
import { getMetricDef } from './canonicalMetrics';

export function formatMetricValue(metric: string, v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  const format = getMetricDef(metric)?.format;

  if (format === 'percent') return `${(v * 100).toFixed(2)}%`;
  if (format === 'ratio') return v.toFixed(2);
  if (format === 'multiple') return `${v.toFixed(2)}×`;
  if (format === 'currency') {
    return v.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: metric === 'spend' ? 0 : 2,
    });
  }
  // count / unknown
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}

/**
 * Compact variant for axis ticks. `formatMetricValue` spells currency out in
 * full ("$606,164"), which is fine in a KPI tile but collides with its
 * neighbours when repeated up a chart axis — so money abbreviates the same way
 * counts do.
 */
export function formatAxisTick(
  metric: string,
  v: number | null | undefined,
  opts?: { percentDecimals?: number },
): string {
  if (v == null || Number.isNaN(v)) return '';
  const format = getMetricDef(metric)?.format;

  if (format === 'currency') {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${Math.round(v / 1_000)}K`;
    return `$${Math.round(v)}`;
  }
  if (format === 'percent' && opts?.percentDecimals != null) {
    return `${(v * 100).toFixed(opts.percentDecimals)}%`;
  }
  return formatMetricValue(metric, v);
}

/**
 * Decimal places that keep an axis of small percentages readable.
 *
 * A fixed 2dp is right for a KPI tile but wrong for an axis: CTR here runs about
 * 0.005%, so consecutive ticks both round to "0.01%" and the axis reads as two
 * identical labels. Scale the precision to the largest tick instead, and apply
 * the SAME count to every tick — varying it per value makes the axis ragged.
 */
export function percentAxisDecimals(maxFraction: number): number {
  const maxPercent = Math.abs(maxFraction) * 100;
  if (maxPercent >= 10) return 1;
  if (maxPercent >= 1) return 2;
  if (maxPercent >= 0.1) return 3;
  return 4;
}

export function metricLabel(metric: string): string {
  return getMetricDef(metric)?.label ?? metric;
}

/**
 * Hover reason for a "—" rendered by `formatMetricValue` on a null ratio
 * metric (flank#5099) — CTR reuses Unified Reporting's own wording
 * (flank#4752) since it's the same clickable-media rule; every other ratio
 * metric falls back to its catalog `derivedFrom` formula, which is always
 * true (and the only wording ribeye-api's own query logic guarantees)
 * without inventing a channel-specific explanation this file can't verify.
 */
export function notApplicableReason(metric: string): string | undefined {
  if (metric === 'ctr') return CTR_CLICKABLE_NOTE;
  const derivedFrom = getMetricDef(metric)?.derivedFrom;
  return derivedFrom ? `No ${metricLabel(metric)} for this selection (${derivedFrom}).` : undefined;
}
